import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';

import { useAuth } from '../providers/auth-provider';

export interface LoginScreenProps {
  externalEmail?: string | null | undefined;
}

export function LoginScreen({ externalEmail }: LoginScreenProps = {}) {
  const { login, verifyCode, error, retryAfterSeconds, needsNewCode } = useAuth();
  const isMfaMode = !!externalEmail;
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(externalEmail ?? '');
  const [code, setCode] = useState('');
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [isCodeSubmitting, setIsCodeSubmitting] = useState(false);
  // Seconds left on a 429's wait: every button that would hit the server
  // stays disabled until it reaches 0.
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (retryAfterSeconds !== null && retryAfterSeconds > 0) {
      setCountdown(Math.ceil(retryAfterSeconds));
    }
  }, [retryAfterSeconds]);

  const counting = countdown > 0;
  useEffect(() => {
    if (!counting) return;
    const id = setInterval(() => setCountdown((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [counting]);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsEmailSubmitting(true);
    try {
      await login(isMfaMode ? externalEmail! : email);
      setIsEmailSubmitting(false);
      setStep('code');
    } catch {
      setIsEmailSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsCodeSubmitting(true);
    try {
      await verifyCode(isMfaMode ? externalEmail! : email, code);
    } catch {
      /* error surfaced via useAuth().error */
    } finally {
      setIsCodeSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setIsEmailSubmitting(true);
    try {
      await login(isMfaMode ? externalEmail! : email);
    } catch {
      /* error surfaced */
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const waitStrip = <ErrorStrip>{`Too many attempts — try again in ${countdown} s`}</ErrorStrip>;
  // The wait replaces the server's detail on whichever step is showing; the
  // code step may also carry the resend prompt next to the detail.
  const emailStrip = counting ? (
    waitStrip
  ) : (
    error && !isEmailSubmitting && <ErrorStrip>{error}</ErrorStrip>
  );
  const codeStrips = counting ? (
    waitStrip
  ) : (
    <>
      {error && !isCodeSubmitting && <ErrorStrip>{error}</ErrorStrip>}
      {needsNewCode && !isCodeSubmitting && (
        <ErrorStrip>This code needs a fresh request — tap Resend code</ErrorStrip>
      )}
    </>
  );

  return (
    <div
      data-testid="login-root"
      style={{
        minHeight: '100%',
        width: '100%',
        background: 'var(--bg)',
        color: 'var(--text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, Helvetica, sans-serif',
      }}
    >
      <div
        data-testid="login-card"
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--surface)',
          borderRadius: 8,
          border: '1px solid var(--border)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
          padding: '36px 32px 28px',
          position: 'relative',
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 400,
            letterSpacing: '0.05em',
            textAlign: 'center',
            margin: 0,
            color: 'var(--text)',
          }}
        >
          <span style={{ fontWeight: 400 }}>shift</span>
          <span style={{ fontWeight: 600 }}>agent</span>
        </h1>
        <p
          style={{
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--text-mute)',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            margin: '6px 0 20px',
            fontWeight: 500,
          }}
        >
          {isMfaMode ? 'Identity verification' : 'Command center'}
        </p>

        {/* MFA email step: locked email + send code */}
        {isMfaMode && step === 'email' && (
          <div data-testid="login-step-mfa">
            <p style={{ fontSize: 13, color: 'var(--text-soft)', textAlign: 'center', marginBottom: 12 }}>
              A verification code will be sent to:
            </p>
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--bg-alt)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                textAlign: 'center',
                marginBottom: 16,
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--text)',
              }}
            >
              {externalEmail}
            </div>
            {isEmailSubmitting && <StatusStrip>Sending verification code…</StatusStrip>}
            {emailStrip}
            <button
              type="button"
              onClick={() => {
                void handleEmailSubmit({ preventDefault: () => {} } as FormEvent);
              }}
              disabled={isEmailSubmitting || counting}
              style={primaryButtonStyle(isEmailSubmitting || counting)}
            >
              {isEmailSubmitting ? 'Sending…' : 'Continue'}
            </button>
          </div>
        )}

        {/* Standard email step */}
        {!isMfaMode && step === 'email' && (
          <form
            data-testid="login-step-email"
            onSubmit={handleEmailSubmit}
            aria-label="Email login form"
          >
            <label
              htmlFor="login-email"
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--text-soft)',
                fontWeight: 500,
                marginBottom: 6,
                letterSpacing: '0.02em',
              }}
            >
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEmailSubmitting}
              style={inputStyle()}
            />
            {isEmailSubmitting && <StatusStrip>Sending verification code…</StatusStrip>}
            {emailStrip}
            <button
              type="submit"
              disabled={isEmailSubmitting || counting}
              style={primaryButtonStyle(isEmailSubmitting || counting)}
            >
              {isEmailSubmitting ? 'Sending…' : 'Continue'}
            </button>
          </form>
        )}

        {/* Code step (shared by both modes) */}
        {step === 'code' && (
          <form
            data-testid="login-step-code"
            onSubmit={handleCodeSubmit}
            aria-label="Code verification form"
          >
            <p
              style={{
                fontSize: 13,
                color: 'var(--text-soft)',
                textAlign: 'center',
                marginBottom: 16,
              }}
            >
              Enter the code sent to{' '}
              <strong style={{ color: 'var(--text)', fontWeight: 500 }}>
                {isMfaMode ? externalEmail : email}
              </strong>
            </p>
            <label
              htmlFor="login-code"
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--text-soft)',
                fontWeight: 500,
                marginBottom: 6,
                letterSpacing: '0.02em',
              }}
            >
              Verification code
            </label>
            <input
              id="login-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isCodeSubmitting}
              style={inputStyle()}
            />
            {isCodeSubmitting && <StatusStrip>Verifying code…</StatusStrip>}
            {codeStrips}
            <button
              type="submit"
              disabled={isCodeSubmitting || counting}
              style={primaryButtonStyle(isCodeSubmitting || counting)}
            >
              {isCodeSubmitting ? 'Verifying…' : 'Verify'}
            </button>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => {
                  void handleResendCode();
                }}
                disabled={isEmailSubmitting || counting}
                style={linkButtonStyle()}
              >
                Resend code
              </button>
              {!isMfaMode && (
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  style={linkButtonStyle()}
                >
                  Back
                </button>
              )}
            </div>
          </form>
        )}
      </div>
      <p
        style={{
          position: 'absolute',
          bottom: 18,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 10,
          color: 'var(--text-mute)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          opacity: 0.7,
        }}
      >
        Secure zero-trust authentication
      </p>
    </div>
  );
}

function StatusStrip({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      style={{
        marginTop: 12,
        padding: '8px 12px',
        background: 'var(--accent-softer)',
        border: '1px solid var(--accent-soft)',
        borderRadius: 4,
        fontSize: 12,
        color: 'var(--accent)',
        fontWeight: 500,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </div>
  );
}

function ErrorStrip({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: '8px 12px',
        background: 'rgba(192, 57, 43, 0.06)',
        border: '1px solid rgba(192, 57, 43, 0.18)',
        borderRadius: 4,
        fontSize: 12,
        color: 'var(--kind-failure)',
        fontWeight: 500,
      }}
    >
      {children}
    </div>
  );
}

function inputStyle(): CSSProperties {
  return {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: 'var(--text)',
    background: 'var(--bg)',
    border: '1px solid var(--border-strong)',
    borderRadius: 6,
    outline: 'none',
    transition: 'border-color 120ms ease',
  };
}

function primaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    width: '100%',
    marginTop: 20,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#ffffff',
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : 1,
    letterSpacing: '0.02em',
    transition: 'background 120ms ease, opacity 120ms ease',
  };
}

function linkButtonStyle(): CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-soft)',
    fontSize: 12,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    fontFamily: 'inherit',
  };
}
